import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/FirebaseAuthContext';
import { Bell, MessageCircle, Heart, Plus, User, Settings, LogOut, LayoutDashboard, Bookmark, Briefcase, Package } from 'lucide-react';
import { notificationAPI } from '../../services/api';

const Header = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [headerLogo, setHeaderLogo] = useState(null);
  const [faviconLogo, setFaviconLogo] = useState(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Update favicon if provided
    if (faviconLogo) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = faviconLogo;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [faviconLogo]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const fetchSettings = async () => {
    try {
      // Use fetch directly for public endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/settings/branding`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setHeaderLogo(data.data.headerLogo);
          setFaviconLogo(data.data.faviconLogo);
        }
      }
    } catch (error) {
      console.debug('Could not fetch branding settings:', error.message);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      setUnreadCount(response.data.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery)}`);
    } else {
      // If search is empty, navigate to home without search params
      navigate('/');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowUserMenu(false);
  };

  return (
    <header className="header">
      <div className="header-content">
        {/* Logo */}
        <Link to="/" className="header-logo">
          {headerLogo ? (
            <img
              src={headerLogo}
              alt="Logo"
              style={{
                maxHeight: '56px',
                maxWidth: '200px',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Heart size={28} fill="currentColor" />
          )}
          <span>BabyResell</span>
        </Link>

        {/* Search */}
        <div className="header-search">
          <form onSubmit={handleSearch}>
            <input
              type="text"
              className="search-input"
              placeholder="Search baby items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </form>
        </div>

        {/* Navigation */}
        <nav className="header-nav">
          {isAuthenticated ? (
            <>
              {/* Create Listing Button */}
              <Link to="/create-listing" className="btn btn-primary">
                <Plus size={20} />
                <span>Sell</span>
              </Link>

              {/* Messages - Hidden on mobile */}
              <Link to="/messages" className="nav-icon mobile-hidden">
                <MessageCircle size={24} />
              </Link>

              {/* Notifications - Hidden on mobile */}
              <Link to="/notifications" className="nav-icon mobile-hidden">
                <Bell size={24} />
                {unreadCount > 0 && (
                  <span className="badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </Link>

              {/* User Menu */}
              <div className="user-menu" ref={userMenuRef}>
                <img
                  src={user?.profileImage || `https://ui-avatars.com/api/?name=${user?.username}&background=e60023&color=fff`}
                  alt={user?.username}
                  className="user-avatar"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                />

                {showUserMenu && (
                  <div className="dropdown-menu">
                    <Link
                      to={`/profile/${user?.username}`}
                      className="dropdown-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <User size={18} />
                      <span>Profile</span>
                    </Link>

                    <Link
                      to="/my-orders"
                      className="dropdown-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Heart size={18} />
                      <span>My Orders</span>
                    </Link>

                    <Link
                      to="/saved-items"
                      className="dropdown-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Bookmark size={18} />
                      <span>Saved Items</span>
                    </Link>

                    <Link
                      to="/seller-dashboard"
                      className="dropdown-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <LayoutDashboard size={18} />
                      <span>Seller Dashboard</span>
                    </Link>

                    {user?.accountType !== 'business' && user?.businessApprovalStatus !== 'pending' && (
                      <Link
                        to="/business/apply"
                        className="dropdown-item"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Briefcase size={18} />
                        <span>Apply for Business Account</span>
                      </Link>
                    )}

                    {(user?.accountType === 'business' || user?.isAdmin) && (
                      <Link
                        to="/bulk-create-listings"
                        className="dropdown-item"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Package size={18} />
                        <span>Bulk Create Listings</span>
                      </Link>
                    )}

                    <Link
                      to="/settings"
                      className="dropdown-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Settings size={18} />
                      <span>Settings</span>
                    </Link>

                    {isAdmin && (
                      <>
                        <div className="dropdown-divider" />
                        <Link
                          to="/admin"
                          className="dropdown-item"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <LayoutDashboard size={18} />
                          <span>Admin Dashboard</span>
                        </Link>
                      </>
                    )}

                    <div className="dropdown-divider" />

                    <button className="dropdown-item" onClick={handleLogout}>
                      <LogOut size={18} />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link">
                Login
              </Link>
              <Link to="/register" className="btn btn-primary">
                Sign Up
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
